import React, { useState } from 'react';
import { X, AlertCircle, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const AuthModal = ({ isOpen, onClose, title, subtitle, showDontShowAgain, onDontShowAgainChange }) => {
  const { continueAsGuest, loginWithGoogle, login, register, isFirebaseConfigured } = useAuth();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  if (!isOpen) return null;
  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setDontShowAgain(checked);
    if (onDontShowAgainChange) {
      onDontShowAgainChange(checked);
    }
  };

  const handleGuestContinue = () => {
    continueAsGuest();
    onClose();
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      await loginWithGoogle();
      onClose();
    } catch (err) {
      console.error('Google login error:', err);
      setError(err.message || 'Failed to sign in with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isSignup) {
        if (!name.trim()) {
          setError('Please enter your name');
          setLoading(false);
          return;
        }
        await register(email, password, name);
      } else {
        await login(email, password);
      }
      onClose();
    } catch (err) {
      console.error('Email auth error:', err);

      // Parse Firebase error codes for user-friendly messages
      let errorMessage = err.message || `Failed to ${isSignup ? 'sign up' : 'sign in'}. Please try again.`;

      if (err.code === 'auth/email-already-in-use' || errorMessage.includes('email-already-in-use')) {
        errorMessage = 'This email is already registered. Please sign in instead, or use "Sign in with Google" if you registered with Google.';
        setIsSignup(false); // Switch to login mode
      } else if (err.code === 'auth/weak-password' || errorMessage.includes('weak-password')) {
        errorMessage = 'Password should be at least 6 characters long.';
      } else if (err.code === 'auth/invalid-email' || errorMessage.includes('invalid-email')) {
        errorMessage = 'Please enter a valid email address.';
      } else if (err.code === 'auth/user-not-found' || errorMessage.includes('user-not-found')) {
        errorMessage = 'No account found with this email. Please sign up first.';
        setIsSignup(true); // Switch to signup mode
      } else if (err.code === 'auth/wrong-password' || errorMessage.includes('wrong-password')) {
        errorMessage = 'Incorrect password. Please try again.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {title || "Sign In"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        <p className="text-gray-600 mb-6">
          {subtitle || "Sign in to save your scores and access additional features, or continue as a guest."}
        </p>

        {showDontShowAgain && (
          <div className="mt-4 flex items-center">
            <input
              type="checkbox"
              id="dontShowAgain"
              checked={dontShowAgain}
              onChange={handleCheckboxChange}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
            <label htmlFor="dontShowAgain" className="ml-2 text-sm text-gray-600">
              Don't show this again in this session
            </label>
          </div>
        )}

        {!isFirebaseConfigured && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded mb-4 flex items-start">
            <AlertCircle size={20} className="mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Authentication Setup Required</p>
              <p className="text-sm">Firebase authentication is not configured yet. You can continue as a guest for now.</p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4 flex items-start">
            <AlertCircle size={20} className="mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {!showEmailForm ? (
            <>
              {/* Google Login - Prominent */}
              <button
                onClick={handleGoogleLogin}
                disabled={!isFirebaseConfigured || loading}
                className={`w-full flex items-center justify-center gap-3 rounded-lg px-4 py-4 text-base font-semibold ${
                  isFirebaseConfigured && !loading
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-lg'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {loading ? 'Signing in...' : 'Continue with Google'}
              </button>

              {/* Email/Password Button */}
              <button
                onClick={() => setShowEmailForm(true)}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 rounded-lg px-4 py-3 bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <Mail size={20} />
                Sign in with Email
              </button>

              <div className="text-center py-2">
                <span className="text-gray-500">or</span>
              </div>

              <button
                onClick={handleGuestContinue}
                disabled={loading}
                className="w-full bg-gray-100 text-gray-700 rounded-lg px-4 py-3 hover:bg-gray-200 disabled:bg-gray-400 border border-gray-300"
              >
                Continue as Guest
              </button>
            </>
          ) : (
            <>
              {/* Email/Password Form */}
              <form onSubmit={handleEmailAuth} className="space-y-4">
                {isSignup && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your full name"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    minLength={6}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || !isFirebaseConfigured}
                  className="w-full bg-blue-600 text-white rounded-lg px-4 py-3 hover:bg-blue-700 disabled:bg-gray-400 font-medium"
                >
                  {loading ? 'Please wait...' : (isSignup ? 'Sign Up' : 'Sign In')}
                </button>

                <button
                  type="button"
                  onClick={() => setShowEmailForm(false)}
                  className="w-full text-gray-600 hover:text-gray-800 text-sm"
                >
                  ← Back to other options
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setIsSignup(!isSignup)}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    {isSignup ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>

        {showDontShowAgain && (
          <div className="mt-4 flex items-center">
            <input
              type="checkbox"
              id="dontShowAgain"
              checked={dontShowAgain}
              onChange={handleCheckboxChange}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
            <label htmlFor="dontShowAgain" className="ml-2 text-sm text-gray-600">
              Don't show this again in this session
            </label>
          </div>
        )}

        {!isFirebaseConfigured && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Currently in demo mode. Authentication will be enabled once Firebase is configured.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
